import React from 'react';
import { Modal, ModalDialog, ModalClose, Typography, Stack, Box, Divider, Chip, Avatar } from '@mui/joy';
import S3_URL from '../../../../utils/s3';

interface MeetingDetailsModalProps {
  open: boolean;
  onClose: () => void;
  meeting: {
    id: string;
    scheduledAt: string;
    duration: number;
    location: string;
    notes: string;
    scheduledBy: {
      uuid: string;
      fullName: string;
    };
    applicants: Array<{
      uuid: string;
      zipCode: string;
      videoUuid: string;
      user: {
        uuid: string;
        fullName: string;
        email: string | null;
        phoneNo: string;
      };
      applicantMeetings: {
        id: string;
        applicantId: string;
        meetingId: string;
        createdAt: string;
        updatedAt: string;
        deletedAt: string | null;
        confirmedAt: string | null;
        declinedAt: string | null;
        invitationSentAt: string | null;
        note: string | null;
      };
    }>;
  } | null;
}

const MeetingDetailsModal: React.FC<MeetingDetailsModalProps> = ({ open, onClose, meeting }) => {
  if (!meeting) return null;

  const startDate = new Date(meeting.scheduledAt);
  const endDate = new Date(startDate.getTime() + meeting.duration);

  const getApplicantStatus = (applicantMeeting: any) => {
    if (applicantMeeting.confirmedAt) {
      return { status: 'confirmed', color: 'success' as const };
    } else if (applicantMeeting.declinedAt) {
      return { status: 'declined', color: 'danger' as const };
    } else if (applicantMeeting.invitationSentAt) {
      return { status: 'invitation sent', color: 'neutral' as const };
    } else {
      return { status: null, color: null };
    }
  };
  
  const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateString = startDate.toLocaleDateString([], { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        size="lg"
        sx={{
          maxWidth: 800,
          width: { xs: '95vw', sm: '90vw' },
          maxHeight: '90vh',
          overflow: 'auto',
          mx: { xs: 1, sm: 0 }
        }}
      >
        <ModalClose />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography level="h3" sx={{ mb: 3, fontWeight: 700 }}>
            Meeting Details
          </Typography>

          <Stack spacing={4}>
            {/* Date and Time */}
            <Box>
              <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
                {dateString}
              </Typography>
              <Typography level="body-lg" sx={{ fontWeight: 600 }}>
                {startTime} - {endTime}
              </Typography>
            </Box>

            <Divider />

            {/* Location */}
            <Box>
              <Typography level="title-md" sx={{ mb: 1, fontWeight: 600 }}>
                Location
              </Typography>
              <Typography level="body-md" sx={{ color: 'neutral.600' }}>
                {meeting.location}
              </Typography>
            </Box>

            {/* Scheduled By */}
            <Box>
              <Typography level="title-md" sx={{ mb: 1, fontWeight: 600 }}>
                Scheduled By
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.500' }}>
                  {meeting.scheduledBy.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                </Avatar>
                <Typography level="body-md">
                  {meeting.scheduledBy.fullName}
                </Typography>
              </Stack>
            </Box>

            {/* Notes */}
            {meeting.notes && (
              <Box>
                <Typography level="title-md" sx={{ mb: 1, fontWeight: 600 }}>
                  Notes
                </Typography>
                <Typography level="body-md" sx={{ color: 'neutral.600' }}>
                  {meeting.notes}
                </Typography>
              </Box>
            )}

            <Divider />

            {/* Applicants */}
            <Box>
              <Typography level="title-md" sx={{ mb: 2, fontWeight: 600 }}>
                Invited People ({meeting.applicants.length})
              </Typography>
              <Stack spacing={2}>
                {meeting.applicants.map((applicant) => (
                  <Box
                    key={applicant.uuid}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'neutral.200',
                      borderRadius: 2,
                      bgcolor: 'background.surface'
                    }}
                  >
                     <Stack 
                       direction={{ xs: 'column', sm: 'row' }} 
                       spacing={{ xs: 1, sm: 2 }} 
                       alignItems={{ xs: 'flex-start', sm: 'center' }}
                     >
                       <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                         <Box sx={{ width: 50, height: 50, flexShrink: 0 }}>
                           <Box
                             component="img"
                             src={`${S3_URL}processed/${applicant.videoUuid}/frames/${applicant.videoUuid}-.0000000.jpg`}
                             alt={applicant.user.fullName}
                             sx={{ 
                               width: '100%',
                               height: '100%',
                               objectFit: 'cover',
                               borderRadius: '8px'
                             }}
                           />
                         </Box>
                         <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                           <Typography level="body-md" sx={{ fontWeight: 600 }}>
                             {applicant.user.fullName}
                           </Typography>
                           {(() => {
                              const { status, color } = getApplicantStatus(applicant.applicantMeetings);
                              return status ? (
                                <Chip 
                                  color={color}
                                  size="sm"
                                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                >
                                  {status}
                                </Chip>
                              ) : null;
                            })()}
                          </Stack>
                           <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                             {applicant.user.phoneNo}
                           </Typography>
                           <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                             Zip: {applicant.zipCode}
                           </Typography>
                           {applicant.applicantMeetings.note && (
                             <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.level1', borderRadius: 1 }}>
                               <Typography level="body-sm" sx={{ fontWeight: 500, color: 'neutral.700', mb: 0.5 }}>
                                 Note from {applicant.user.fullName}:
                               </Typography>
                               <Typography level="body-sm" sx={{ color: 'neutral.600', fontStyle: 'italic' }}>
                                 "{applicant.applicantMeetings.note}"
                               </Typography>
                             </Box>
                           )}
                         </Box>
                       </Stack>
                       
                     </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default MeetingDetailsModal; 